/**
 * Volume Profile Plugin for LightweightCharts v5
 * Renders volume profile histogram as a native chart plugin
 *
 * Shows volume distribution at each price level with:
 * - POC (Point of Control) - highest volume price
 * - Value Area High (VAH) - top of 70% volume area
 * - Value Area Low (VAL) - bottom of 70% volume area
 *
 * Supports multiple days - renders one volume profile per trading day,
 * each anchored to its Globex open time.
 *
 * Real-time updates via SSE (Server-Sent Events) for today's profile.
 * Historical days fetched on-demand via REST when scrolling/panning.
 */

import { CONFIG } from '../config';
import { authFetch } from '../auth/authFetch';
import { unifiedSSE } from '../hooks/useUnifiedSSE';

// Histogram width in logical px: single source for every primitive creation site.
const HISTOGRAM_WIDTH_PX = 150;

// Primitive GC: when panning accumulates primitives, detach the ones whose day
// is further than GC_MARGIN_DAYS outside the visible range (fetched data stays
// cached for cheap re-attach). Skipped entirely below GC_KEEP_MIN primitives.
const GC_MARGIN_DAYS = 7;
const GC_KEEP_MIN = 15;

// Gate for per-fetch/per-draw console noise; console.error always logs.
const DEBUG = false;
const dlog = (...args) => { if (DEBUG) console.log(...args); };

// Resolve a session-anchor timestamp (RTH/Globex open) to an x-coordinate. Exact match
// first, so on a time-uniform chart (where the open is always an exact candle time) the
// behaviour is unchanged. Otherwise snap to the CLOSEST bar, but ONLY when the anchor
// falls strictly BETWEEN existing bars -- this is the renko case, where bricks form on
// price crossings so there is no brick at exactly 9:30 ET and timeToCoordinate returns
// null. Outside the data range returns null (the original skip). NOTE: renko display
// times are de-collided formation seconds, so "closest" is the nearest brick by display
// time (approximately, not exactly, the 9:30 brick); the plugin only sees series.data().
function anchorCoordinate(chart, series, ts) {
  const exact = chart.timeScale().timeToCoordinate(ts);
  if (exact !== null && exact !== undefined) return exact;
  const data = (series && series.data) ? series.data() : null;
  if (!data || data.length < 2) return null;
  if (ts <= data[0].time || ts >= data[data.length - 1].time) return null;
  let lo = 0, hi = data.length - 1;
  while (lo < hi - 1) { const mid = (lo + hi) >> 1; if (data[mid].time <= ts) lo = mid; else hi = mid; }
  const nearest = (ts - data[lo].time) <= (data[hi].time - ts) ? data[lo].time : data[hi].time;
  return chart.timeScale().timeToCoordinate(nearest);
}

class VolumeProfileRenderer {
  constructor(profileData, series, chart, options = {}) {
    this.profileData = profileData;
    this.series = series;
    this.chart = chart;
    this.options = {
      histogramWidth: options.histogramWidth || HISTOGRAM_WIDTH_PX,
      pocColor: options.pocColor || 'rgba(251, 191, 36, 0.6)',
      coreValueAreaColor: options.coreValueAreaColor || 'rgba(251, 146, 60, 0.55)',
      valueAreaColor: options.valueAreaColor || 'rgba(38, 166, 154, 0.45)',
      outsideColor: options.outsideColor || 'rgba(107, 114, 128, 0.3)',
      outlineColor: options.outlineColor || 'rgba(255, 255, 255, 0.15)',
      // Delta profile colors
      showDeltaProfile: options.showDeltaProfile || false,
      deltaPositiveColor: options.deltaPositiveColor || 'rgba(34, 197, 94, 0.6)',  // Green for buying
      deltaNegativeColor: options.deltaNegativeColor || 'rgba(239, 68, 68, 0.6)',  // Red for selling
      deltaWidth: options.deltaWidth || 100,
      ...options
    };
  }

  draw(target) {
    if (!this.profileData || !this.series) {
      return;
    }

    const { price_levels, volumes, poc, vah, val, cvah, cval, tick_size, deltas } = this.profileData;
    const tickSize = tick_size || 0.25;

    // Skip if no data
    if (!price_levels || price_levels.length === 0) {
      return;
    }

    // Get canvas context
    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context;
      const { horizontalPixelRatio, verticalPixelRatio } = scope;

      // Position histogram anchored to session open time
      // RTH profiles anchor at rth_open_ts; overnight/full anchor at globex_open_ts
      const histogramLogicalWidth = this.options.histogramWidth;
      const deltaLogicalWidth = this.options.deltaWidth;
      let histogramX;

      if (this.chart) {
        const sessionType = this.profileData.meta.session_type;
        const anchorTs = sessionType === 'rth'
          ? this.profileData.rth_open_ts
          : this.profileData.globex_open_ts;

        if (!anchorTs) return;

        const anchorX = anchorCoordinate(this.chart, this.series, anchorTs);
        if (anchorX !== null && anchorX !== undefined) {
          histogramX = anchorX * horizontalPixelRatio;
        } else {
          return;
        }
      } else {
        return;
      }

      const maxBarWidth = histogramLogicalWidth * horizontalPixelRatio;
      const maxDeltaWidth = deltaLogicalWidth * horizontalPixelRatio;

      ctx.save();

      // Bar height is constant on a linear price scale — compute once from the
      // first resolvable level instead of 2 extra priceToCoordinate calls per level.
      // (price_levels arrive sorted ascending from the server — no per-frame sort.)
      let barHeight = 1;
      for (let i = 0; i < price_levels.length; i++) {
        const yA = this.series.priceToCoordinate(price_levels[i] + tickSize / 2);
        const yB = this.series.priceToCoordinate(price_levels[i] - tickSize / 2);
        if (yA !== null && yB !== null) {
          barHeight = Math.max(Math.abs((yB - yA) * verticalPixelRatio), 1);
          break;
        }
      }
      const halfBarHeight = barHeight / 2;

      // Draw each volume bar (extends RIGHT from anchor)
      for (let i = 0; i < price_levels.length; i++) {
        const price = price_levels[i];
        const volume = volumes[i];
        const delta = deltas ? deltas[i] : 0;

        // Get the price coordinate (logical pixels)
        const priceY = this.series.priceToCoordinate(price);

        if (priceY === null) continue;

        const y = priceY * verticalPixelRatio - halfBarHeight;

        // Calculate bar width based on volume (0-100 normalized)
        const barWidth = (volume / 100) * maxBarWidth;

        // Determine color based on price level (POC > CVA > VA > outside)
        const tolerance = tickSize / 2;
        const isPOC = Math.abs(price - poc) < tolerance;
        const isCoreValueArea = cvah && cval && price >= (cval - tolerance) && price <= (cvah + tolerance);
        const isValueArea = price >= (val - tolerance) && price <= (vah + tolerance);

        let color;
        if (isPOC) {
          color = this.options.pocColor;
        } else if (isCoreValueArea) {
          color = this.options.coreValueAreaColor;
        } else if (isValueArea) {
          color = this.options.valueAreaColor;
        } else {
          color = this.options.outsideColor;
        }

        // Draw volume bar (extends RIGHT)
        ctx.fillStyle = color;
        ctx.fillRect(histogramX, y, barWidth, barHeight);

        // Draw outline
        ctx.strokeStyle = this.options.outlineColor;
        ctx.lineWidth = 0.5;
        ctx.strokeRect(histogramX, y, barWidth, barHeight);

        // Draw delta bar (extends LEFT from anchor) if delta profile is enabled
        if (this.options.showDeltaProfile && delta !== undefined && delta !== 0) {
          // Delta is -100 to +100 normalized
          // Positive = more buying (green), Negative = more selling (red)
          const deltaBarWidth = (Math.abs(delta) / 100) * maxDeltaWidth;
          const deltaColor = delta >= 0 ? this.options.deltaPositiveColor : this.options.deltaNegativeColor;

          // Draw delta bar extending LEFT from anchor point
          ctx.fillStyle = deltaColor;
          ctx.fillRect(histogramX - deltaBarWidth, y, deltaBarWidth, barHeight);

          // Draw outline
          ctx.strokeStyle = this.options.outlineColor;
          ctx.lineWidth = 0.5;
          ctx.strokeRect(histogramX - deltaBarWidth, y, deltaBarWidth, barHeight);
        }
      }

      // Draw extending lines for POC, VAH, VAL from histogram right edge to session end
      const chartWidth = scope.bitmapSize.width;
      const lineStartX = histogramX + maxBarWidth;
      const lineEndX = this._sessionEndX(this.profileData.session_end_ts, chartWidth, horizontalPixelRatio);

      const drawExtendingLine = (price, color, lineWidth, dashed, label) => {
        const priceY = this.series.priceToCoordinate(price);
        if (priceY === null) return;
        const y = Math.round(priceY * verticalPixelRatio);

        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        if (dashed) {
          ctx.setLineDash([6 * horizontalPixelRatio, 4 * horizontalPixelRatio]);
        } else {
          ctx.setLineDash([]);
        }
        ctx.moveTo(lineStartX, y);
        ctx.lineTo(lineEndX, y);
        ctx.stroke();
        ctx.setLineDash([]);

        // Inline label at the right end of the line, vertically centered (OI-levels style)
        if (label) {
          const fontSize = Math.round(10 * verticalPixelRatio);
          ctx.font = `${fontSize}px sans-serif`;
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          const textWidth = ctx.measureText(label).width;
          const padding = 3 * horizontalPixelRatio;
          const labelX = lineEndX - padding * 2;
          ctx.fillStyle = 'rgba(15, 20, 25, 0.85)';
          ctx.fillRect(
            labelX - textWidth - padding,
            y - fontSize / 2 - padding,
            textWidth + padding * 2,
            fontSize + padding * 2
          );
          ctx.fillStyle = color;
          ctx.fillText(label, labelX, y);
        }
      };

      if (poc) {
        drawExtendingLine(poc, 'rgba(251, 191, 36, 0.7)', 2 * horizontalPixelRatio, false, 'Vol POC');
      }
      if (vah) {
        drawExtendingLine(vah, 'rgba(96, 165, 250, 0.5)', 1 * horizontalPixelRatio, true, 'Vol VAH');
      }
      if (val) {
        drawExtendingLine(val, 'rgba(96, 165, 250, 0.5)', 1 * horizontalPixelRatio, true, 'Vol VAL');
      }
      if (cvah) {
        drawExtendingLine(cvah, 'rgba(251, 146, 60, 0.5)', 1 * horizontalPixelRatio, true, 'CVAH');
      }
      if (cval) {
        drawExtendingLine(cval, 'rgba(251, 146, 60, 0.5)', 1 * horizontalPixelRatio, true, 'CVAL');
      }

      ctx.restore();
    });
  }
  _sessionEndX(endTs, chartWidth, hRatio) {
    if (!endTs) return chartWidth;
    const coord = this.chart.timeScale().timeToCoordinate(endTs);
    if (coord !== null) return Math.round(coord * hRatio);
    const data = this.series.data();
    if (!data || data.length === 0) return chartWidth;
    let lo = 0, hi = data.length - 1;
    if (data[0].time > endTs) return chartWidth;
    // Session end at/beyond the last loaded bar = the forming session: extend to the pane edge
    if (data[hi].time <= endTs) return chartWidth;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (data[mid].time <= endTs) lo = mid; else hi = mid;
    }
    const c = this.chart.timeScale().timeToCoordinate(data[lo].time);
    return c !== null ? Math.round(c * hRatio) : chartWidth;
  }
}

class VolumeProfilePrimitiveView {
  constructor(profileData, series, chart, options) {
    this._renderer = new VolumeProfileRenderer(profileData, series, chart, options);
  }

  renderer() {
    return this._renderer;
  }

  updateProfileData(profileData) {
    this._renderer.profileData = profileData;
  }

  updateOptions(options) {
    this._renderer.options = { ...this._renderer.options, ...options };
  }
}

class VolumeProfilePrimitive {
  constructor(profileData, series, chart, options) {
    this._view = new VolumeProfilePrimitiveView(profileData, series, chart, options);
    this._requestUpdate = null;
    this._rafPending = false;
  }

  /**
   * Called when the primitive is attached to the series.
   * Captures the requestUpdate callback for triggering redraws.
   */
  attached({ requestUpdate }) {
    this._requestUpdate = requestUpdate;
  }

  /**
   * Called when the primitive is detached from the series.
   */
  detached() {
    this._requestUpdate = null;
    this._rafPending = false;
  }

  updateAllViews() {
    return this._view;
  }

  paneViews() {
    return [this._view];
  }

  _scheduleUpdate() {
    if (this._requestUpdate && !this._rafPending) {
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        if (this._requestUpdate) this._requestUpdate();
      });
    }
  }

  updateProfileData(profileData) {
    this._view.updateProfileData(profileData);
    this._scheduleUpdate();
  }

  updateOptions(options) {
    this._view.updateOptions(options);
    this._scheduleUpdate();
  }
}

/**
 * Volume Profile Plugin
 * Integrates volume profile visualization with LightweightCharts
 * Supports multiple days - renders one profile per trading day
 */
export class VolumeProfilePlugin {
  constructor(chart, series, chartContainer) {
    this.chart = chart;
    this.series = series;
    this.chartContainer = chartContainer;

    // Store primitives per day (keyed by date string)
    this.primitives = new Map();  // date -> VolumeProfilePrimitive
    this.profileDataByDate = new Map();  // date -> profile data

    this.enabled = false;
    this.sessionType = 'eth'; // Default: full session (ETH) for volume profile
    this.sessionMode = 'full'; // 'full' = single profile, 'split' = overnight + RTH
    this.period = 'daily';
    this.showDeltaProfile = false; // Delta profile toggle

    this.lastFetchParams = null;

    // SSE unsubscribe functions for real-time updates
    this._sseUnsubs = [];

    // Visible range tracking for on-demand fetching
    this.visibleRangeSubscription = null;
    this.pendingFetches = new Set();  // Track in-flight fetches to avoid duplicates
    this.visibleRangeDebounceTimer = null;
  }

  /**
   * Get visible date range from the chart's time scale
   * Returns only the days that are currently visible on screen
   */
  _getVisibleDateRange() {
    if (!this.chart) return null;

    const timeScale = this.chart.timeScale();
    const visibleRange = timeScale.getVisibleLogicalRange();

    if (!visibleRange) return null;

    // Convert logical range to time range
    const visibleTimeRange = timeScale.getVisibleRange();
    if (!visibleTimeRange) return null;

    const startDate = new Date(visibleTimeRange.from * 1000).toISOString().split('T')[0];
    const endDate = new Date(visibleTimeRange.to * 1000).toISOString().split('T')[0];

    return { startDate, endDate };
  }

  /**
   * Subscribe to visible range changes to fetch profiles on-demand
   */
  _subscribeToVisibleRangeChanges() {
    if (!this.chart || this.visibleRangeSubscription) return;

    this.visibleRangeSubscription = this.chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
      // Debounce to avoid excessive API calls while scrolling
      if (this.visibleRangeDebounceTimer) {
        clearTimeout(this.visibleRangeDebounceTimer);
      }

      this.visibleRangeDebounceTimer = setTimeout(() => {
        this._fetchVisibleProfiles();
        this._gcPrimitives();
      }, 300);  // 300ms debounce
    });
  }

  /**
   * Unsubscribe from visible range changes
   */
  _unsubscribeFromVisibleRangeChanges() {
    if (this.visibleRangeSubscription && this.chart) {
      this.chart.timeScale().unsubscribeVisibleLogicalRangeChange(this.visibleRangeSubscription);
      this.visibleRangeSubscription = null;
    }
    if (this.visibleRangeDebounceTimer) {
      clearTimeout(this.visibleRangeDebounceTimer);
      this.visibleRangeDebounceTimer = null;
    }
  }

  /**
   * Get the session types to fetch based on current mode
   */
  _getSessionTypes() {
    // 'split'/'eth-rth' => overnight + RTH side by side; 'rth' => RTH only;
    // anything else ('full') => single ETH profile. Additive: the main chart
    // passes 'full'/'split', the planning chart passes 'full'/'eth-rth'/'rth'.
    if (this.sessionMode === 'split' || this.sessionMode === 'eth-rth') return ['overnight', 'rth'];
    if (this.sessionMode === 'rth') return ['rth'];
    return ['eth'];
  }

  /**
   * Build a cache key for profile data (supports split mode)
   */
  _profileKey(dateStr, sessionType) {
    return sessionType === 'eth' ? dateStr : `${dateStr}:${sessionType}`;
  }

  /**
   * Fetch profiles for currently visible days only
   */
  async _fetchVisibleProfiles() {
    if (!this.enabled || !this.lastFetchParams) return;

    const visibleRange = this._getVisibleDateRange();
    if (!visibleRange) return;

    const { symbol, timeframe } = this.lastFetchParams;
    const visibleDays = this._getTradingDays(visibleRange.startDate, visibleRange.endDate);
    const sessionTypes = this._getSessionTypes();

    // Build fetch tasks for missing (date, sessionType) combos
    const fetchTasks = [];
    for (const dateStr of visibleDays) {
      for (const st of sessionTypes) {
        const key = this._profileKey(dateStr, st);
        if (!this.profileDataByDate.has(key) && !this.pendingFetches.has(key)) {
          fetchTasks.push({ dateStr, sessionType: st, key });
        }
      }
    }

    if (fetchTasks.length === 0) return;

    dlog(`[VOLUME PROFILE] Fetching ${fetchTasks.length} profiles (mode=${this.sessionMode})`);

    // Mark as pending
    fetchTasks.forEach(t => this.pendingFetches.add(t.key));

    // Fetch in parallel
    const fetchPromises = fetchTasks.map(({ dateStr, sessionType, key }) =>
      this._fetchSingleProfile(symbol, dateStr, timeframe, sessionType)
        .then(data => {
          this.pendingFetches.delete(key);
          return { key, data };
        })
        .catch(err => {
          this.pendingFetches.delete(key);
          console.error(`Failed to fetch profile for ${key}:`, err);
          return { key, data: null };
        })
    );

    const results = await Promise.all(fetchPromises);

    // Store results and create primitives for new data
    let newProfilesAdded = false;
    for (const { key, data } of results) {
      if (data) {
        this.profileDataByDate.set(key, data);
        newProfilesAdded = true;

        if (this.enabled) {
          this._createPrimitiveForDate(key, data);
        }
      }
    }

    if (newProfilesAdded) {
      dlog(`[VOLUME PROFILE] Now have ${this.profileDataByDate.size} profiles cached`);
    }
  }

  /**
   * Create a primitive for a single date
   */
  _createPrimitiveForDate(dateStr, profileData) {
    // Don't create duplicate primitives
    if (this.primitives.has(dateStr)) return;

    const primitive = new VolumeProfilePrimitive(profileData, this.series, this.chart, {
      histogramWidth: HISTOGRAM_WIDTH_PX,
      showDeltaProfile: this.showDeltaProfile
    });

    this.series.attachPrimitive(primitive);
    this.primitives.set(dateStr, primitive);

    dlog(`Volume Profile added for ${dateStr}: POC=${profileData.poc?.toFixed(2)}`);
  }

  /**
   * Get list of trading days between start and end dates
   */
  _getTradingDays(startDate, endDate) {
    const days = [];
    const start = new Date(startDate + 'T00:00:00Z');
    const end = new Date(endDate + 'T00:00:00Z');

    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getUTCDay();
      // Skip Saturday (6) and Sunday (0)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        days.push(current.toISOString().split('T')[0]);
      }
      current.setUTCDate(current.getUTCDate() + 1);
    }

    return days;
  }

  /**
   * Handle a volume profile update from SSE (snapshot or live)
   */
  _handleProfileUpdate(data) {
    if (!this.enabled || !data || !data.price_levels) return;

    const dateStr = data.meta.date;
    const sessionType = data.meta.session_type;

    // Only accept updates for session types the current mode actually renders
    // (full => eth; split/eth-rth => overnight+rth; rth => rth).
    if (!this._getSessionTypes().includes(sessionType)) return;

    const key = this._profileKey(dateStr, sessionType);

    // Store/update profile data
    this.profileDataByDate.set(key, data);

    // Update or create primitive
    const primitive = this.primitives.get(key);
    if (primitive) {
      primitive.updateProfileData(data);
    } else if (this.enabled) {
      this._createPrimitiveForDate(key, data);
    }
  }

  /**
   * Connect to SSE for real-time volume profile updates
   */
  _connectSSE() {
    this._disconnectSSE();

    this._sseUnsubs.push(
      unifiedSSE.subscribe('volume-profile', 'volume_profile_snapshot', (data) => {
        dlog('[VOLUME PROFILE] SSE snapshot received');
        this._handleProfileUpdate(data);
      })
    );

    this._sseUnsubs.push(
      unifiedSSE.subscribe('volume-profile', 'volume_profile_update', (data) => {
        this._handleProfileUpdate(data);
      })
    );

    dlog('[VOLUME PROFILE] SSE connected (unified)');
  }

  /**
   * Disconnect SSE
   */
  _disconnectSSE() {
    if (this._sseUnsubs.length > 0) {
      this._sseUnsubs.forEach(unsub => unsub());
      this._sseUnsubs = [];
      dlog('[VOLUME PROFILE] SSE disconnected');
    }
  }

  /**
   * Fetch volume profile data for a single date from API
   */
  async _fetchSingleProfile(symbol, dateStr, timeframe = '5min', sessionType = null) {
    try {
      const st = sessionType || this.sessionType;
      const url = `${CONFIG.apiUrl}/api/indicators/volume-profile?symbol=${symbol}&start_date=${dateStr}&end_date=${dateStr}&session_type=${st}&period=${this.period}&tick_size=0.25`;

      const response = await authFetch(url);

      if (!response.ok) {
        console.error(`[VOLUME PROFILE] Request failed for ${dateStr}:`, response.status);
        return null;
      }

      const data = await response.json();

      if (!data.price_levels || data.price_levels.length === 0) {
        dlog(`[VOLUME PROFILE] No data for ${dateStr}`);
        return null;
      }

      dlog(`[VOLUME PROFILE] ${dateStr}: POC=${data.poc?.toFixed(2)}, bins=${data.price_levels.length}`);
      return data;
    } catch (error) {
      console.error(`Error fetching volume profile for ${dateStr}:`, error);
      return null;
    }
  }

  /**
   * Fetch volume profiles for VISIBLE days only (not entire range)
   * Uses chart's visible time range to determine which days to fetch
   */
  async fetchProfiles(symbol, startDate, endDate, timeframe = '5min') {
    this.lastFetchParams = { symbol, startDate, endDate, timeframe };

    // Get visible range from chart - only fetch what's on screen
    const visibleRange = this._getVisibleDateRange();
    let tradingDays;

    if (visibleRange) {
      tradingDays = this._getTradingDays(visibleRange.startDate, visibleRange.endDate);
      dlog(`[VOLUME PROFILE] Fetching ${tradingDays.length} VISIBLE trading days (mode=${this.sessionMode})`);
    } else {
      tradingDays = this._getTradingDays(startDate, endDate);
    }

    const sessionTypes = this._getSessionTypes();

    // Build fetch tasks for all (date, session) combos
    const fetchTasks = [];
    for (const dateStr of tradingDays) {
      for (const st of sessionTypes) {
        fetchTasks.push({ dateStr, sessionType: st, key: this._profileKey(dateStr, st) });
      }
    }

    // Fetch in parallel
    const fetchPromises = fetchTasks.map(({ dateStr, sessionType, key }) =>
      this._fetchSingleProfile(symbol, dateStr, timeframe, sessionType)
        .then(data => ({ key, data }))
    );

    const results = await Promise.all(fetchPromises);

    for (const { key, data } of results) {
      if (data) {
        this.profileDataByDate.set(key, data);
      }
    }

    dlog(`[VOLUME PROFILE] Fetched ${fetchTasks.length} profiles, total cached: ${this.profileDataByDate.size}`);
    return this.profileDataByDate;
  }

  /**
   * Ensure a primitive exists for every cached profile: create the missing
   * ones, update the existing ones in place. No detach/reattach churn.
   */
  draw() {
    if (!this.enabled || this.profileDataByDate.size === 0) {
      return;
    }

    dlog(`Drawing ${this.profileDataByDate.size} volume profile(s)...`);

    for (const [key, profileData] of this.profileDataByDate) {
      const primitive = this.primitives.get(key);
      if (primitive) {
        primitive.updateProfileData(profileData);
      } else {
        this._createPrimitiveForDate(key, profileData);
      }
    }
  }

  /**
   * Clear/remove all indicators
   */
  clear() {
    // Remove all primitives
    for (const primitive of this.primitives.values()) {
      this.series.detachPrimitive(primitive);
    }
    this.primitives.clear();
  }

  /**
   * Enable the indicator
   */
  async enable(symbol, startDate, endDate, timeframe = '5min') {
    this.enabled = true;

    // Subscribe to visible range changes for on-demand fetching
    this._subscribeToVisibleRangeChanges();

    // Fetch only visible days initially
    await this.fetchProfiles(symbol, startDate, endDate, timeframe);

    if (this.profileDataByDate.size > 0) {
      this.draw();
    } else {
      dlog('[VOLUME PROFILE] No data available for current parameters');
    }

    // Always connect SSE — data may arrive after initial fetch
    this._connectSSE();
  }

  /**
   * Disable the indicator
   */
  disable() {
    this._disconnectSSE();
    this._unsubscribeFromVisibleRangeChanges();
    this.enabled = false;
    this.clear();
    this.profileDataByDate.clear();
    this.pendingFetches.clear();
  }

  /**
   * Toggle the indicator on/off
   */
  async toggle(symbol, startDate, endDate, timeframe = '5min') {
    if (this.enabled) {
      this.disable();
    } else {
      await this.enable(symbol, startDate, endDate, timeframe);
    }
    return this.enabled;
  }

  /**
   * Update with new date range
   * Only fetches newly visible days, reuses cached data
   */
  async update(symbol, startDate, endDate, timeframe = '5min') {
    if (!this.enabled) return;

    // Update params for on-demand fetching
    this.lastFetchParams = { symbol, startDate, endDate, timeframe };

    // Fetch any missing visible days (reuses cache)
    await this._fetchVisibleProfiles();

    if (this.profileDataByDate.size === 0) {
      dlog('[VOLUME PROFILE] No data available, clearing display');
      this.clear();
      return;
    }

    // Reuse existing primitives; only create the missing ones
    // (detach/attach churn on every range change is wasteful).
    this.draw();
    this._gcPrimitives();
  }

  /**
   * Detach primitives whose day is far outside the visible range. The fetched
   * data stays in profileDataByDate, so panning back re-attaches without a
   * refetch. Without this, primitives accumulate unboundedly while panning.
   */
  _gcPrimitives() {
    if (this.primitives.size <= GC_KEEP_MIN) return;
    const visibleRange = this._getVisibleDateRange();
    if (!visibleRange) return;
    const lo = this._shiftDate(visibleRange.startDate, -GC_MARGIN_DAYS);
    const hi = this._shiftDate(visibleRange.endDate, GC_MARGIN_DAYS);
    for (const [key, primitive] of this.primitives) {
      const day = key.split(':')[0];
      if (day < lo || day > hi) {
        this.series.detachPrimitive(primitive);
        this.primitives.delete(key);
      }
    }
  }

  _shiftDate(dateStr, days) {
    const d = new Date(dateStr + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().split('T')[0];
  }

  /**
   * Check if enabled
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Get profile data for a specific date
   */
  getProfileData(dateStr = null) {
    if (dateStr) {
      return this.profileDataByDate.get(dateStr);
    }
    // Return all profiles
    return Object.fromEntries(this.profileDataByDate);
  }

  /**
   * Set session type
   */
  setSessionType(sessionType) {
    this.sessionType = sessionType.toLowerCase();
  }

  /**
   * Get session type
   */
  getSessionType() {
    return this.sessionType;
  }

  /**
   * Set period
   */
  setPeriod(period) {
    this.period = period.toLowerCase();
  }

  /**
   * Get period
   */
  getPeriod() {
    return this.period;
  }

  /**
   * Set delta profile visibility
   * @param {boolean} show - Whether to show delta profile
   */
  setShowDeltaProfile(show) {
    this.showDeltaProfile = show;
    // In-place option update on every attached primitive: no detach/reattach
    // churn, redraw happens on the next animation frame.
    for (const primitive of this.primitives.values()) {
      primitive.updateOptions({ showDeltaProfile: show });
    }
  }

  /**
   * Set session mode ('full' | 'split' | 'eth-rth' | 'rth')
   * Full      = single ETH profile per day
   * Split / eth-rth = overnight + RTH profiles per day
   * RTH       = RTH-only profile per day
   */
  async setSessionMode(mode) {
    if (mode === this.sessionMode) return;
    this.sessionMode = mode;
    if (this.enabled && this.lastFetchParams) {
      // Clear current display and cached data, then re-fetch with new mode
      this.clear();
      this.profileDataByDate.clear();
      this.pendingFetches.clear();
      const { symbol, startDate, endDate, timeframe } = this.lastFetchParams;
      await this.fetchProfiles(symbol, startDate, endDate, timeframe);
      if (this.profileDataByDate.size > 0) {
        this.draw();
      }
    }
  }

  /**
   * Get session mode
   */
  getSessionMode() {
    return this.sessionMode;
  }

  /**
   * Get delta profile visibility
   */
  getShowDeltaProfile() {
    return this.showDeltaProfile;
  }

  /**
   * Toggle delta profile visibility
   */
  toggleDeltaProfile() {
    this.setShowDeltaProfile(!this.showDeltaProfile);
    return this.showDeltaProfile;
  }
}

export default VolumeProfilePlugin;
